import { defineStore } from "pinia";
import {
  decideAccessGate,
  createProducerOrganisation,
  fetchMyAccess,
  fetchOrganisations,
  type ProducerSignupResult,
  fetchPermissions,
  mintOrgToken,
  readLastActiveOrgId,
  rememberLastActiveOrgId,
  resolveAccessibleOrganisations,
  TenantAccessDeniedError,
} from "@/auth/session";
import { Auth0InvalidGrantError, handleRedirectCallback, loginWithRedirect, logout as auth0Logout } from "@/auth/auth0";
import { redeemPendingInvite } from "@/auth/invite";
import {
  decodeAuth0Identity,
  decodeJwtExpiryMs,
  decodeMetaShipUser,
  refreshAuth0TokensLocked,
  setStoredAuth0RefreshToken,
} from "@/auth/tokenStore";
import { RefreshScheduler } from "@/auth/refreshScheduler";
import { filterNavForTenant, type TenantNavModule } from "@/nav/navFilter";
import { isTenantUsable } from "@/tenant/availability";
import type { HostUser } from "@/state/globalState";
import type { NavBarItem, ResolveTenantAppResponse, UserOrganisation } from "@/tenant/types";
import type { TenantState } from "@tenant-theme";

export type SessionStatus =
  | "resolving"
  | "unavailable"
  | "signed-out"
  | "authenticating"
  | "org-pick"
  | "no-access"
  /**
   * DEV-937 — signed in, no organisation, and this app accepts sign-ups. The
   * farm form renders instead of "ask an administrator", which is a dead end
   * for the one visitor the app most wants.
   */
  | "needs-org"
  | "ready"
  | "error";

interface SessionState {
  /** The hostname resolved against `GET /tenant-apps/resolve` (Appendix A2 carries no hostname field). */
  host: string;
  tenant: ResolveTenantAppResponse | null;
  status: SessionStatus;
  errorMessage: string | null;
  accessibleOrgs: UserOrganisation[];
  currentOrg: UserOrganisation | null;
  user: HostUser | null;
  navItems: NavBarItem[];
  /** Bridge bearer between Auth0 and an org selection — never persisted. */
  auth0Bearer: string | null;
  /** Current org-scoped Metaship token's `exp` claim (ms epoch) — drives the refresh scheduler. */
  tokenExpiryMs: number | null;
}

/**
 * One refresh timer for the app's lifetime — module-scoped (not store state)
 * for the same reason `ApiV3.js` keeps `refreshPromise` at module scope: it's
 * a runtime handle, not serialisable session data, and there is exactly one
 * session in this shell.
 */
let refreshScheduler: RefreshScheduler | null = null;

function auth0UserToHostUser(user: {
  sub: string;
  email: string;
  given_name?: string;
  family_name?: string;
  name?: string;
  picture?: string;
}): HostUser {
  const firstname = user.given_name || user.name?.split(" ")[0] || "";
  const surname = user.family_name || user.name?.split(" ").slice(1).join(" ") || "";
  return {
    id: 0, // placeholder — overwritten from the minted Metaship JWT the moment an org is selected (see selectOrganisation)
    email: user.email,
    firstname,
    surname,
    emailAddress: user.email,
    avatar: user.picture,
    auth0Id: user.sub,
  };
}

export const useSessionStore = defineStore("standalone-session", {
  state: (): SessionState => ({
    host: "",
    tenant: null,
    status: "resolving",
    errorMessage: null,
    accessibleOrgs: [],
    currentOrg: null,
    user: null,
    navItems: [],
    auth0Bearer: null,
    tokenExpiryMs: null,
  }),
  getters: {
    tenantModules(state): TenantNavModule[] {
      return state.tenant?.modules ?? [];
    },
  },
  actions: {
    /**
     * `isDevHost` lets a DRAFT/PROVISIONING/FAILED app be exercised via
     * `VITE_TENANT_HOST` on localhost before its provisioner run reaches
     * LIVE — see `tenant/availability.ts`. DISABLED/DELETED/`found:false`
     * stay Unavailable everywhere, dev included.
     */
    setTenant(host: string, tenant: ResolveTenantAppResponse, isDevHost = false) {
      this.host = host;
      this.tenant = tenant;
      this.status = isTenantUsable(tenant, isDevHost) ? "signed-out" : "unavailable";
      if (tenant.found && tenant.slug && tenant.kind && tenant.name && tenant.branding) {
        const tenantState: TenantState = {
          slug: tenant.slug,
          kind: tenant.kind,
          name: tenant.name,
          hostname: host,
          branding: tenant.branding,
        };
        window.MetaShipGlobalState?.updateState({ tenant: tenantState });
      }
    },

    /** Abandon an in-flight/failed auth0 callback and return to the sign-in screen. */
    returnToLogin() {
      this.errorMessage = null;
      this.auth0Bearer = null;
      this.status = "signed-out";
    },

    fail(message: string) {
      this.errorMessage = message;
      this.status = "error";
    },

    beginLogin(prompt?: string, screenHint?: string) {
      return loginWithRedirect({
        ...(prompt ? { prompt } : {}),
        ...(screenHint ? { screenHint } : {}),
      });
    },

    /** `/auth0/callback` → exchange the code, then resolve org access. */
    async completeAuth0Callback(search: string) {
      this.status = "authenticating";
      const result = await handleRedirectCallback(search);
      this.auth0Bearer = `Bearer ${result.token}`;
      this.user = auth0UserToHostUser(result.user);
      if (result.refreshToken) setStoredAuth0RefreshToken(result.refreshToken);
      // DEV-1044 — an invited grower has NO organisation until the invite gives
      // them one, so this must run before the access gate, not after it.
      // Best-effort: a failed redeem falls through to the normal gate rather
      // than stranding a legitimately signed-in user on an error.
      await redeemPendingInvite(this.auth0Bearer);
      await this.resolveOrgAccess();
    },

    /**
     * Merge the identity claims of an Auth0 id_token onto `user`, keeping any
     * numeric Metaship id already resolved (the id_token has no such claim —
     * only a minted Metaship token does, see `selectOrganisation`).
     */
    applyAuth0Identity(idToken: string | null | undefined) {
      const identity = decodeAuth0Identity(idToken);
      if (!identity) return;
      this.user = {
        id: this.user?.id ?? 0,
        email: identity.email || this.user?.email || "",
        firstname: identity.firstname || this.user?.firstname || "",
        surname: identity.surname || this.user?.surname || "",
        emailAddress: identity.email || this.user?.emailAddress || "",
        avatar: identity.avatar ?? this.user?.avatar ?? null,
        auth0Id: identity.sub || this.user?.auth0Id,
        tagId: this.user?.tagId ?? null,
      };
    },

    /**
     * Reload fast-path: the previous visit's Metaship token is still persisted
     * and unexpired on `window.MetaShipGlobalState` — skip the whole Auth0
     * round trip. Returns `false` (leaving `status` untouched) when there's
     * nothing usable, so the caller falls through to `resumeFromRefreshToken`.
     */
    resumeFromPersistedSession(): boolean {
      const persisted = window.MetaShipGlobalState?.getState();
      if (!persisted?.token || !persisted.orgToken || !persisted.currentOrg || !persisted.user) return false;
      if (persisted.tokenExpiry && Date.now() > persisted.tokenExpiry) return false;

      this.user = persisted.user;
      this.currentOrg = persisted.currentOrg;
      this.accessibleOrgs = persisted.organisations ?? [persisted.currentOrg];
      this.navItems = persisted.permissions ?? [];
      this.tokenExpiryMs = persisted.tokenExpiry ?? null;
      this.status = "ready";
      this.startRefreshScheduler();
      // The restored nav is a SNAPSHOT of whatever /user/permission returned on
      // a previous visit — it goes stale when the org's modules change, and an
      // empty snapshot (a failed fetch, or a shell build that filtered
      // everything out) would otherwise stick forever, leaving a sidebar with
      // nothing but "Home" in it. Re-fetch in the background; the restored list
      // stays on screen until the fresh one lands.
      void this.refreshNavigation(persisted.token, persisted.orgToken);
      return true;
    },

    /**
     * Re-fetch + re-filter the sidebar nav for the live token/org. Silent by
     * design: this runs on a session that is ALREADY usable, so a failure must
     * leave the visitor exactly where they are rather than surfacing an error.
     */
    async refreshNavigation(bearer?: string | null, orgToken?: string | null): Promise<void> {
      const persisted = window.MetaShipGlobalState?.getState();
      const activeBearer = bearer ?? persisted?.token ?? null;
      const activeOrgToken = orgToken ?? persisted?.orgToken ?? null;
      if (!activeBearer || !activeOrgToken) return;
      try {
        const navItems = await fetchPermissions(activeBearer, activeOrgToken);
        this.navItems = filterNavForTenant(navItems, this.tenantModules);
        window.MetaShipGlobalState?.updateState({ permissions: this.navItems });
      } catch {
        // Keep the restored nav — a transient /user/permission failure is not
        // a reason to empty the sidebar.
      }
    },

    /**
     * Silent resume from a stored Auth0 refresh token (page reload with no
     * live Metaship token). The refresh token is only ever discarded when
     * Auth0 itself rejects it (`Auth0InvalidGrantError`) — a downstream
     * failure (e.g. `/user/permission` 500, a network blip) leaves it intact
     * and just surfaces an error, since the credential itself is still good.
     */
    async resumeFromRefreshToken() {
      this.status = "authenticating";

      // A previous visit may have persisted `user` (with its real numeric
      // Metaship id) even though its token has now expired — rehydrate it so
      // `resolveOrgAccess`'s lastActiveOrg lookup uses the numeric id instead
      // of falling back to having no hint at all.
      const persistedUser = window.MetaShipGlobalState?.getState()?.user;
      if (persistedUser?.id) this.user = persistedUser;

      let fresh;
      try {
        fresh = await refreshAuth0TokensLocked();
      } catch (e) {
        if (e instanceof Auth0InvalidGrantError) setStoredAuth0RefreshToken(null);
        this.status = "signed-out";
        throw e;
      }
      if (!fresh.id_token) {
        this.status = "signed-out";
        throw new Error("Auth0 refresh returned no id_token");
      }
      this.auth0Bearer = `Bearer ${fresh.id_token}`;
      // A silent resume never touches the userinfo response `completeAuth0Callback`
      // reads, so the identity has to come out of the id_token itself. Without
      // this the session ran all the way to "ready" with `user === null`: the top
      // bar showed a "?" monogram, every module reading `user.id` off the global
      // state got nothing, and `readLastActiveOrgId(this.user.id)` below could
      // never fire — so a multi-org visitor was sent to the organisation picker
      // on every single visit.
      this.applyAuth0Identity(fresh.id_token);

      // Same as the callback path: someone who already had a session in this
      // tab can still be arriving on an invite link.
      await redeemPendingInvite(this.auth0Bearer);

      try {
        await this.resolveOrgAccess();
      } catch (e) {
        this.fail(e instanceof Error ? e.message : "Could not restore your session");
        throw e;
      }
    },

    /**
     * DEV-937 — create the organisation, then walk the normal post-login path.
     *
     * On success the caller is a member of a brand-new (or freshly claimed)
     * organisation, so `resolveOrgAccess` now finds one and auto-selects it —
     * reusing the same token-mint and permission-load every other sign-in runs,
     * rather than a second bespoke path that would drift from it.
     */
    async completeSignup(farm: {
      farmName: string;
      contactName?: string;
      contactPhone?: string;
      province?: string;
    }): Promise<ProducerSignupResult> {
      if (!this.tenant?.slug) throw new Error("completeSignup called before the tenant resolved");
      const bearer = await this.ensureAuth0Bearer();
      const result = await createProducerOrganisation(this.tenant.slug, bearer, farm);

      // NEEDS_REVIEW means a human has to decide (a claim on an organisation
      // that already has members, or a duplicate business id). There is nothing
      // to sign into yet, so the caller shows the reference instead.
      if (result.outcome === "NEEDS_REVIEW") return result;

      await this.resolveOrgAccess();
      return result;
    },

    /** organisations → my-access filter → gate decision (auto-select / picker / NoAccess). */
    async resolveOrgAccess() {
      if (!this.tenant?.slug || !this.auth0Bearer) {
        throw new Error("resolveOrgAccess called before tenant/auth0Bearer set");
      }
      const [allOrgs, myAccess] = await Promise.all([
        fetchOrganisations(this.auth0Bearer),
        fetchMyAccess(this.tenant.slug, this.auth0Bearer),
      ]);
      this.accessibleOrgs = resolveAccessibleOrganisations(allOrgs, myAccess);

      const lastActiveOrgId = this.user?.id != null ? readLastActiveOrgId(this.user.id) : null;
      const decision = decideAccessGate(this.accessibleOrgs, lastActiveOrgId);
      if (decision.mode === "none") {
        // An orgless visitor is the SIGN-UP case on an app that accepts them,
        // and only a dead end on one that does not.
        this.status = this.tenant?.selfServeSignup === true ? "needs-org" : "no-access";
        return;
      }
      if (decision.mode === "pick") {
        this.status = "org-pick";
        return;
      }
      await this.selectOrganisation(decision.org);
    },

    /**
     * Silently renew the Auth0 credential if it's missing or close to
     * expiring, so the org switcher still works after a plain page reload
     * (`resumeFromPersistedSession` never sets `auth0Bearer` — it restores
     * straight from the persisted Metaship token, which has nothing to do
     * with the Auth0 side).
     */
    async ensureAuth0Bearer(): Promise<string> {
      if (this.auth0Bearer) {
        const expMs = decodeJwtExpiryMs(this.auth0Bearer);
        if (expMs == null || Date.now() < expMs) return this.auth0Bearer;
      }
      let fresh;
      try {
        fresh = await refreshAuth0TokensLocked();
      } catch (e) {
        if (e instanceof Auth0InvalidGrantError) setStoredAuth0RefreshToken(null);
        throw e;
      }
      if (!fresh.id_token) throw new Error("Auth0 refresh returned no id_token");
      this.auth0Bearer = `Bearer ${fresh.id_token}`;
      return this.auth0Bearer;
    },

    /** Mint the org-scoped Metaship token, fetch + filter the nav, and mark the session ready. */
    async selectOrganisation(org: UserOrganisation) {
      if (!this.tenant?.slug) throw new Error("selectOrganisation called before tenant set");
      const auth0Bearer = await this.ensureAuth0Bearer();

      let minted;
      try {
        minted = await mintOrgToken(org, auth0Bearer, this.tenant.slug);
      } catch (e) {
        // Server-side truth: this org is not part of the app, even though
        // my-access said otherwise (revoked between calls) — same terminal
        // state as the my-access empty-list case (binding design addendum).
        if (e instanceof TenantAccessDeniedError) {
          this.denyAccess();
          return;
        }
        throw e;
      }
      if (!minted?.token) throw new Error("Organisation token mint refused");

      const bearer = `Bearer ${minted.token}`;
      const navItems = await fetchPermissions(bearer, org.token);
      const expiryMs = decodeJwtExpiryMs(bearer);

      // The MINTED Metaship JWT (not the Auth0 id_token) carries the real
      // numeric Metaship user id — the Auth0 userinfo placeholder (id: 0)
      // never resolves to it on its own. Without this, `user.id` stayed 0
      // forever (published to every module reading it — orders/api.ts,
      // bookings/bookingWizard.ts, finance-v2/hub.ts) and the `if (this.user
      // ?.id)` guard below was permanently falsy, so lastActiveOrg was NEVER
      // written.
      //
      // The `&& this.user` half of the old guard was the second half of the
      // same bug: on any path that reached here WITHOUT an Auth0 identity, the
      // minted token's own `{ id, firstname, surname, emailAddress }` was
      // thrown away too and the session stayed anonymous. The token is a
      // complete identity on its own — use it as the base when there is none.
      const metaShipUser = decodeMetaShipUser(bearer);
      if (metaShipUser) {
        this.user = {
          id: metaShipUser.id,
          email: metaShipUser.emailAddress || this.user?.email || "",
          firstname: metaShipUser.firstname || this.user?.firstname || "",
          surname: metaShipUser.surname || this.user?.surname || "",
          emailAddress: metaShipUser.emailAddress || this.user?.emailAddress || "",
          avatar: this.user?.avatar ?? null,
          auth0Id: this.user?.auth0Id,
          tagId: metaShipUser.tagId,
        };
      }

      this.currentOrg = org;
      this.navItems = filterNavForTenant(navItems, this.tenantModules);
      this.tokenExpiryMs = expiryMs;
      this.status = "ready";
      if (this.user?.id != null) rememberLastActiveOrgId(this.user.id, org.id);

      window.MetaShipGlobalState?.updateState({
        token: bearer,
        tokenExpiry: expiryMs,
        orgToken: org.token,
        currentOrg: org,
        organisations: this.accessibleOrgs,
        permissions: this.navItems,
        user: this.user,
      });

      this.startRefreshScheduler();

      return { bearer, orgToken: org.token, expiryMs };
    },

    /**
     * Proactive refresh: silently renew the Auth0 credential from the stored
     * rotating refresh token, re-mint the org-scoped Metaship token, and
     * republish `token`/`tokenExpiry`/`orgToken` to the global state (+ its
     * `vuex` mirror) in one atomic `updateState` call. Ported from
     * `ApiV3.js` `refreshAccessToken` (:96-120) + `auth0Config.js`
     * `refreshAuth0Tokens` (:304-327) — ONE difference: this shell has no
     * per-request interceptor to trigger it, so `RefreshScheduler` calls it
     * on a timer instead of on the next outgoing request.
     *
     * Any thrown error here (Auth0-rejected, transient network, tenant
     * access denied) is classified by `RefreshScheduler`/`denyAccess`, NOT
     * by this method — it just does the work and lets failures propagate.
     */
    async refreshSession(): Promise<void> {
      if (!this.currentOrg) throw new Error("refreshSession called with no active organisation");
      if (!this.tenant?.slug) throw new Error("refreshSession called before tenant set");

      const fresh = await refreshAuth0TokensLocked();
      if (!fresh.id_token) throw new Error("Auth0 refresh returned no id_token");

      let minted;
      try {
        minted = await mintOrgToken(this.currentOrg, `Bearer ${fresh.id_token}`, this.tenant.slug);
      } catch (e) {
        if (e instanceof TenantAccessDeniedError) {
          // Access to this app was revoked mid-session. `denyAccess()` stops
          // the scheduler (so it does NOT also see the now-null tokenExpiryMs
          // and race a second, full sign-out on top of this one) and swaps to
          // NoAccess — swallow rather than rethrow so RefreshScheduler's
          // generic catch doesn't ALSO call signOut() over this.
          this.denyAccess();
          return;
        }
        throw e;
      }
      if (!minted?.token) throw new Error("Organisation token refresh refused");

      const bearer = `Bearer ${minted.token}`;
      this.tokenExpiryMs = decodeJwtExpiryMs(bearer);

      window.MetaShipGlobalState?.updateState({
        token: bearer,
        tokenExpiry: this.tokenExpiryMs,
        orgToken: this.currentOrg.token,
      });
    },

    /** Idempotent — safe to call every time the session becomes ready (boot resume, org switch). */
    startRefreshScheduler() {
      refreshScheduler?.stop();
      refreshScheduler = new RefreshScheduler({
        getExpiryMs: () => this.tokenExpiryMs,
        refresh: () => this.refreshSession(),
        signOut: (clearRefreshToken) => this.signOut({ clearRefreshToken }),
      });
      refreshScheduler.start();
    },

    stopRefreshScheduler() {
      refreshScheduler?.stop();
      refreshScheduler = null;
    },

    /**
     * Server said this org is not part of the app (a `TenantAccessDeniedError`
     * from `POST /user-access/refresh-token`) — route to NoAccess, same
     * terminal UI as the my-access empty-list case, and locally terminate the
     * live session (clear org/nav/token, wipe the global state so mounted
     * modules lose their credentials) WITHOUT the full Auth0 `/v2/logout`
     * redirect — that stays reserved for the user's own "Sign out" click on
     * the NoAccess screen.
     */
    denyAccess() {
      this.stopRefreshScheduler();
      this.currentOrg = null;
      this.accessibleOrgs = [];
      this.navItems = [];
      this.tokenExpiryMs = null;
      this.status = "no-access";
      window.MetaShipGlobalState?.updateState({
        token: null,
        tokenExpiry: null,
        orgToken: null,
        currentOrg: null,
        organisations: null,
        permissions: null,
      });
    },

    /**
     * `clearRefreshToken` defaults to `true` for every explicit, user-facing
     * call (the "Sign out" button). `RefreshScheduler` passes `false` for a
     * transient-failure sign-out — the stored refresh token is still good,
     * so a fresh page load can silently resume instead of forcing a full
     * re-login (see `decideRefreshFailure`).
     */
    signOut(options: { clearRefreshToken?: boolean } = {}) {
      const clearRefreshToken = options.clearRefreshToken ?? true;
      this.stopRefreshScheduler();
      this.currentOrg = null;
      this.user = null;
      this.navItems = [];
      this.accessibleOrgs = [];
      this.auth0Bearer = null;
      this.tokenExpiryMs = null;
      this.status = "signed-out";
      window.MetaShipGlobalState?.updateState({
        token: null,
        tokenExpiry: null,
        orgToken: null,
        currentOrg: null,
        organisations: null,
        permissions: null,
        user: null,
      });
      if (clearRefreshToken) {
        // A genuine, final sign-out (explicit click, or Auth0 itself
        // rejected the credential) — discard the refresh token AND bounce
        // through Auth0's own logout. A transient-failure give-up (RT still
        // possibly good, see `decideRefreshFailure`) only resets LOCAL
        // state: forcing an external navigation on top of a network issue
        // would be actively worse, and the kept RT lets the next page load
        // silently resume instead of demanding a full re-login.
        setStoredAuth0RefreshToken(null);
        auth0Logout();
      }
    },
  },
});
